<template>
  <div class="smart-hassan-id-card-report">
    <!-- Control Bar (Hidden on Print) -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <div class="brand-badge mr-2">
          <feather-icon icon="CreditCardIcon" size="20" class="text-white" />
        </div>
        <h4 class="mb-0 font-weight-bolder">
          The Smart School <span class="text-primary">| Student ID Cards</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2 px-1">
          {{ reportItems.length }} Students Loaded
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All Cards</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-wrapper">
      <div class="loader-content">
        <b-spinner
          variant="danger"
          style="width: 4rem; height: 4rem"
          class="mb-3"
        />
        <h3 class="font-weight-bold text-danger">
          Generating The Smart School ID Cards
        </h3>
        <p class="text-muted">
          Please wait while we process the student records...
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5 error-wrapper">
      <div class="error-icon-bg mb-3">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger" />
      </div>
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button
        variant="outline-danger"
        @click="loadReportData"
        class="mt-3 round"
      >Try Again</b-button>
    </div>

    <!-- Cards Container -->
    <div v-else>
      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 no-data-state"
      >
        <div class="no-data-icon">
          <feather-icon icon="InboxIcon" size="64" />
        </div>
        <h3 class="mt-2 font-weight-bold">No Students Found</h3>
        <p class="text-muted">
          Try adjusting your filters to find student records.
        </p>
      </div>

      <div v-else class="print-pages">
        <div
          v-for="(page, pageIndex) in cardPages"
          :key="'page-' + pageIndex"
          class="print-page"
          :class="`print-page--count-${page.length}`"
        >
          <div class="cards-grid">
            <div
              v-for="(student, index) in page"
              :key="student.id || student.grNo || `${pageIndex}-${index}`"
              class="student-id-block"
            >
              <template v-for="cardType in cardTypes">
              <!-- FRONT SIDE OF CARD -->
              <div
                :key="`front-${cardType.key}-${student.id || student.grNo || index}`"
                class="id-card id-card--front"
              >
                <!-- Top Curved Red Header -->
                <div class="card-header-red card-header-red--front">
                  <!-- Smooth downward arched red background -->
                  <svg class="front-header-arc" viewBox="0 0 257 125" preserveAspectRatio="none">
                    <path d="M0,0 L257,0 L257,72 Q128.5,122 0,72 Z" fill="#AB010B" />
                  </svg>

                  <div class="header-top-row">
                    <!-- Left: Campus Logo / TSS Badge -->
                    <div class="tss-badge">
                      <img
                        v-if="hasCampusLogo"
                        :src="campusLogoUrl"
                        alt="Campus Logo"
                        class="campus-logo-img"
                        @error="onCampusLogoError"
                      />
                      <img
                        v-else
                        src="/smart_school_badge.png"
                        alt="The Smart School"
                        class="smart-school-badge-img"
                      />
                    </div>

                    <!-- Center: Script Title + Campus -->
                    <div class="header-center-info">
                      <div class="school-script-title">The Smart School</div>
                      <div class="campus-bar">
                        <span class="campus-bar__dash"></span>
                        <span class="campus-bar__name">{{ displayCampusName }}</span>
                        <span class="campus-bar__dash"></span>
                      </div>
                    </div>

                    <!-- Right: City School Crest Seal -->
                    <div class="city-crest">
                      <img
                        src="/city_school_crest.png"
                        alt="The City School"
                        class="city-crest-img"
                      />
                      <div class="crest-since">Since 1978</div>
                    </div>
                  </div>
                </div>

                <!-- Front Card Body -->
                <div class="card-body-front">
                  <!-- Student Photo Frame -->
                  <div class="photo-frame-wrapper">
                    <div class="photo-frame">
                      <img
                        v-if="hasStudentPhoto(student)"
                        :src="studentPhotoUrl(student)"
                        :alt="studentDisplayName(student)"
                        class="photo-frame__img"
                        referrerpolicy="no-referrer"
                        @error="handleImgError($event, student)"
                      />
                      <div v-else class="photo-placeholder">
                        <svg class="camera-icon" viewBox="0 0 24 24" fill="none" stroke="#7f8c8d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                          <circle cx="12" cy="13" r="4"></circle>
                        </svg>
                        <span class="photo-placeholder-text">PHOTO HERE</span>
                      </div>
                    </div>
                  </div>

                  <!-- Student Info Fields -->
                  <div class="student-fields">
                    <!-- Row 1: Student Name -->
                    <div class="field-row">
                      <div class="field-icon-box">
                        <svg viewBox="0 0 24 24" class="field-icon" fill="#ffffff">
                          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                      </div>
                      <div class="field-content">
                        <div class="field-label">STUDENT NAME</div>
                        <div class="field-line">
                          <span class="field-value">{{ studentDisplayName(student) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Row 2: Father's Name -->
                    <div class="field-row">
                      <div class="field-icon-box">
                        <svg viewBox="0 0 24 24" class="field-icon" fill="#ffffff">
                          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                      </div>
                      <div class="field-content">
                        <div class="field-label">FATHER'S NAME</div>
                        <div class="field-line">
                          <span class="field-value">{{ studentFatherName(student) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Row 3: Class | Section | G.R. No. -->
                    <div class="field-row">
                      <div class="field-icon-box">
                        <svg viewBox="0 0 24 24" class="field-icon" fill="#ffffff">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                        </svg>
                      </div>
                      <div class="field-content field-content--triplet">
                        <div class="triplet-col">
                          <div class="field-label">CLASS</div>
                          <div class="field-line">
                            <span class="field-value">{{ getStudentClass(student) }}</span>
                          </div>
                        </div>
                        <div class="triplet-divider"></div>
                        <div class="triplet-col">
                          <div class="field-label">SECTION</div>
                          <div class="field-line">
                            <span class="field-value">{{ getStudentSection(student) }}</span>
                          </div>
                        </div>
                        <div class="triplet-divider"></div>
                        <div class="triplet-col">
                          <div class="field-label">G.R. NO.</div>
                          <div class="field-line">
                            <span class="field-value">{{ getStudentGr(student) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Row 4: Contact -->
                    <div class="field-row">
                      <div class="field-icon-box">
                        <svg viewBox="0 0 24 24" class="field-icon" fill="#ffffff">
                          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                        </svg>
                      </div>
                      <div class="field-content">
                        <div class="field-label">CONTACT</div>
                        <div class="field-line">
                          <span class="field-value">{{ getStudentContact(student) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Bottom Decorative Light Grey Wave -->
                <div class="bottom-waves-wrap">
                  <svg class="bottom-waves-svg" viewBox="0 0 204 32" preserveAspectRatio="none">
                    <path d="M0,18 Q60,30 130,12 T204,18 L204,32 L0,32 Z" fill="#e9ecf0" />
                    <path d="M0,24 Q70,12 140,26 T204,16 L204,32 L0,32 Z" fill="#dce1e7" opacity="0.6" />
                  </svg>
                </div>

                <!-- Red Footer -->
                <div class="card-footer-red">
                  <span class="footer-dash"></span>
                  <span class="footer-title">{{ cardType.label }}</span>
                  <span class="footer-dash"></span>
                </div>
              </div>

              <!-- BACK SIDE OF CARD -->
              <div
                :key="`back-${cardType.key}-${student.id || student.grNo || index}`"
                class="id-card id-card--back"
              >
                <!-- Top Flat Red Header -->
                <div class="card-header-red card-header-red--back">
                  <div class="header-top-row">
                    <!-- Left: Campus Logo / TSS Badge -->
                    <div class="tss-badge">
                      <img
                        v-if="hasCampusLogo"
                        :src="campusLogoUrl"
                        alt="Campus Logo"
                        class="campus-logo-img"
                        @error="onCampusLogoError"
                      />
                      <img
                        v-else
                        src="/smart_school_badge.png"
                        alt="The Smart School"
                        class="smart-school-badge-img"
                      />
                    </div>

                    <!-- Center: Script Title + Campus -->
                    <div class="header-center-info">
                      <div class="school-script-title">The Smart School</div>
                      <div class="campus-bar">
                        <span class="campus-bar__dash"></span>
                        <span class="campus-bar__name">{{ displayCampusName }}</span>
                        <span class="campus-bar__dash"></span>
                      </div>
                    </div>

                    <!-- Right: City School Crest Seal -->
                    <div class="city-crest">
                      <img
                        src="/city_school_crest.png"
                        alt="The City School"
                        class="city-crest-img"
                      />
                      <div class="crest-since">Since 1978</div>
                    </div>
                  </div>
                </div>

                <!-- Back Card Body -->
                <div class="card-body-back">
                  <!-- Triple Stripe Divider with Centered Book Emblem -->
                  <div class="back-mid-stripes">
                    <div class="stripes-left">
                      <span class="stripe-line"></span>
                      <span class="stripe-line"></span>
                      <span class="stripe-line"></span>
                    </div>

                    <div class="book-emblem-badge">
                      <img
                        src="/smart_school_book_circle.png"
                        alt="The Smart School"
                        class="book-circle-img"
                      />
                    </div>

                    <div class="stripes-right">
                      <span class="stripe-line"></span>
                      <span class="stripe-line"></span>
                      <span class="stripe-line"></span>
                    </div>
                  </div>

                  <!-- Return Instruction & Campus Info -->
                  <div class="back-return-info">
                    <div class="return-label">If found, please return to:</div>

                    <!-- Campus Name with Red Pin -->
                    <div class="campus-main-entry">
                      <feather-icon
                        icon="MapPinIcon"
                        size="20"
                        class="campus-main-pin"
                      />
                      <div class="campus-main-title">{{ displayCampusName }}</div>
                    </div>

                    <!-- Campus Address with Pin -->
                    <div class="campus-address-entry">
                      <div class="pin-icon-small">
                        <svg viewBox="0 0 24 24" class="loc-pin-svg" fill="#9d0b0f">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      </div>
                      <div class="address-text">{{ displayCampusAddress }}</div>
                    </div>

                    <!-- Thin Divider Line -->
                    <div class="back-divider-line"></div>

                    <!-- Campus Email with Red Envelope -->
                    <div class="contact-entry">
                      <div class="contact-icon">
                        <svg viewBox="0 0 24 24" class="mail-svg" fill="#9d0b0f">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                        </svg>
                      </div>
                      <div class="contact-text">{{ displayCampusEmail }}</div>
                    </div>

                    <!-- Campus Facebook with Red 'f' -->
                    <div class="contact-entry">
                      <div class="contact-icon fb-icon">
                        <svg viewBox="0 0 24 24" class="fb-svg" fill="#9d0b0f">
                          <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
                        </svg>
                      </div>
                      <div class="contact-text">{{ displayCampusFacebook }}</div>
                    </div>
                  </div>
                </div>

                <!-- Bottom Decorative Light Grey Wave -->
                <div class="bottom-waves-wrap">
                  <svg class="bottom-waves-svg" viewBox="0 0 204 32" preserveAspectRatio="none">
                    <path d="M0,18 Q60,30 130,12 T204,18 L204,32 L0,32 Z" fill="#e9ecf0" />
                    <path d="M0,24 Q70,12 140,26 T204,16 L204,32 L0,32 Z" fill="#dce1e7" opacity="0.6" />
                  </svg>
                </div>

                <!-- Red Footer: Moto & Project -->
                <div class="card-footer-red card-footer-red--back">
                  <div class="back-moto">“ I AM – TO LEARN ”</div>
                  <div class="back-project">A Project of The City School</div>
                </div>
              </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";

export default {
  name: "SmartHassanIDCard",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "",
      campusName: "",
      campusAddress: "",
      campusPhone: "",
      campusEmail: "",
      campusFacebook: "",
      campusLogo: "",
      campusLogoFailed: false,
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessYear: "",
      sessionID: 0,
      studentIDs: [],
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      cardTypes: [
        { key: "student", label: "STUDENT CARD" },
        { key: "parents", label: "PARENTS CARD" },
      ],
      defaultPlaceholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e8ecf0'/%3E%3Ccircle cx='50' cy='38' r='20' fill='%23adb5bd'/%3E%3Cpath d='M10 100 Q10 68 50 68 Q90 68 90 100Z' fill='%23adb5bd'/%3E%3C/svg%3E",
    };
  },
  computed: {
    hasCampusLogo() {
      return !!this.campusLogo && !this.campusLogoFailed;
    },
    campusLogoUrl() {
      if (!this.campusLogo) return "";
      if (
        String(this.campusLogo).startsWith("blob:") ||
        String(this.campusLogo).startsWith("data:")
      ) {
        return this.campusLogo;
      }
      return getMediaPreviewUrl(this.campusLogo, "Image");
    },
    headerSchoolName() {
      return (this.schoolName || "The Smart School").trim();
    },
    displayCampusName() {
      return (this.campusName || "").trim();
    },
    displayCampusAddress() {
      return (
        this.campusAddress ||
        "A28, Bukhari Town, Block 10A, Gulshan e Iqbal, Karachi."
      ).trim();
    },
    displayCampusEmail() {
      return (this.campusEmail || "tssgulshanx@gmail.com").trim();
    },
    displayCampusFacebook() {
      return (
        this.campusFacebook || "facebook.com/tss.gulshanx.campus"
      ).trim();
    },
    cardPages() {
      const pageSize = 2;
      const pages = [];
      const items = this.reportItems || [];

      for (let i = 0; i < items.length; i += pageSize) {
        pages.push(items.slice(i, i + pageSize));
      }

      return pages;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    onCampusLogoError() {
      this.campusLogoFailed = true;
    },
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData?.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      if (query.studentIDs) {
        this.studentIDs = query.studentIDs
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
      }

      this.status =
        this.studentIDs.length > 0 ? "all" : query.status || "present";
      this.sessYear = query.sess || "2026-2027";
      this.sessionID = query.sessionID || 0;

      if (!this.db) {
        this.error = "System context missing. Please reload from dashboard.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData?.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.schoolName = match.organizationName || match.orgName || "";
            this.campusName = match.branch || match.name || "";
            this.campusAddress = match.address || match.branch || "";
            this.campusPhone =
              match.phone || match.contact || match.mobile || "";
            this.campusEmail = match.email || "";
            this.campusFacebook = match.facebook || match.website || "";
            this.campusLogo =
              match.logo || match.image || match.branchLogo || "";
          }
        }
      } catch (e) {
        console.warn("Campus branding failed to load", e);
      }
    },
    getStudentClass(student) {
      if (!student) return "—";
      return (
        student.className ||
        student.cls ||
        student.meta_class ||
        "—"
      );
    },
    getStudentSection(student) {
      if (!student) return "—";
      return (
        student.sectionName ||
        student.section ||
        student.secName ||
        "—"
      );
    },
    getStudentGr(student) {
      if (!student) return "—";
      return student.grNo || student.gr || student.gr_no || "—";
    },
    getStudentContact(student) {
      if (!student) return this.campusPhone || "—";
      return (
        student.mobile ||
        student.phone ||
        student.father_mobile ||
        student.emergency_contact ||
        student.contact ||
        this.campusPhone ||
        "—"
      );
    },
    studentFatherName(student) {
      const name =
        student?.father_name || student?.fatherName || student?.father || "";
      return String(name).trim() || "—";
    },
    normalizeStudentList(responseData) {
      if (!responseData) return [];
      if (!Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      if (Array.isArray(responseData)) return responseData;
      return [];
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      const headers = {
        Authorization: `Bearer ${this.$store.state.userData?.token}`,
        accept: "application/json",
      };

      const sessionID = this.sessionID || this.$store.state.sessionID || 0;

      try {
        let response;

        if (this.studentIDs.length > 0) {
          const url = `${this.$store.state.domain}Students/LoadDataSelected`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              ids: this.studentIDs.join(","),
              status: "all",
              sessionID,
            },
            headers,
          });
        } else {
          const url = `${this.$store.state.domain}Students/LoadDataAll`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              clsID: this.classID || 0,
              section: this.sectionID || 0,
              status: this.status,
              sessionID,
            },
            headers,
          });
        }

        if (response.data && response.data.status === "success") {
          this.reportItems = this.normalizeStudentList(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data;
        } else {
          this.error =
            response.data?.message ||
            "The server did not return valid student records.";
        }
      } catch (err) {
        console.error("Data retrieval error:", err);
        this.error = "Unable to connect to school records system.";
      } finally {
        this.loading = false;
      }
    },
    hasStudentPhoto(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      return !!url && url !== this.defaultPlaceholder;
    },
    studentPhotoUrl(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (!url) return this.defaultPlaceholder;
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    studentDisplayName(student) {
      const name =
        student?.name || student?.studentName || student?.student_name || "";
      return String(name).trim() || "—";
    },
    handleImgError(e, student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (url && !String(url).startsWith("blob:")) {
        handleDrivePreviewError(e, url, "image");
        return;
      }
      e.target.src = this.defaultPlaceholder;
    },
    print() {
      window.print();
    },
    close() {
      if (this.studentIDs && this.studentIDs.length > 0) {
        this.$router.push({ name: "manage-studentgrid" });
      } else {
        this.$router.push({ name: "report" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Alex+Brush&family=Dancing+Script:wght@700&family=Great+Vibes&family=Montserrat:wght@400;500;600;700;800;900&display=swap");

$smart-red: #AB010B;
$smart-red-dark: #8c0008;
$smart-gold: #fdb813;
$text-dark: #1a1a1a;
$line-gray: #4a4a4a;
$card-w: 6.8cm;
$card-h: 9.5cm;

.smart-hassan-id-card-report {
  background: #eaedf2;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Montserrat", sans-serif;
  color: $text-dark;
}

/* Control Bar */
.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  padding: 1rem 2rem;
  margin-bottom: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.6);

  .brand-badge {
    background: linear-gradient(135deg, $smart-red 0%, #d63031 100%);
    padding: 8px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(157, 11, 15, 0.35);
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 0.7rem 1.6rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  outline: none;
  font-size: 0.88rem;

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, $smart-red 0%, #c0392b 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(157, 11, 15, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(157, 11, 15, 0.45);
  }
}

.btn-close {
  background: white;
  color: #555;
  border: 1px solid #dcdde1;

  &:hover {
    background: #fff;
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

/* Page & Grid Setup */
.print-pages {
  width: 100%;
}

.print-page + .print-page {
  margin-top: 32px;
}

.cards-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px 28px;
  width: 100%;
  margin: 0 auto;
}

.student-id-block {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: auto;
  max-width: 100%;
  break-inside: avoid;
  page-break-inside: avoid;
}

/* Card Container */
.id-card {
  width: $card-w;
  height: $card-h;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* =========================================
   CARD HEADER (SHARED FRONT & BACK)
   ========================================= */
.card-header-red {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  z-index: 2;

  &--front {
    background: transparent;
    height: 98px;
    padding: 8px 8px 0;

    .front-header-arc {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }

    .header-top-row {
      position: relative;
      z-index: 2;
    }
  }

  &--back {
    background: $smart-red;
    padding: 8px 8px 8px;
  }
}

.header-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
}

/* Left TSS / Campus Logo Badge */
.tss-badge {
  background: #ffffff;
  border-radius: 4px;
  padding: 1px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25cm;
  height: 30px;
  flex-shrink: 0;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  .campus-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform: scale(1.55);
    display: block;
  }

  .smart-school-badge-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

/* Center Header Script & Campus */
.header-center-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 0 4px;
}

.school-script-title {
  font-family: "Great Vibes", "Alex Brush", "Brush Script MT", cursive;
  font-size: 13.5pt;
  line-height: 1.1;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  letter-spacing: 0.01em;
  text-align: center;
  max-width: 100%;
  margin-top: -1px;
  margin-bottom: 2px;
}

.campus-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 0 2px;

  &__dash {
    height: 1.2px;
    background: #ffffff;
    width: 12px;
    flex-shrink: 0;
    opacity: 0.95;
  }

  &__name {
    font-size: 6.4pt;
    font-weight: 800;
    color: #ffffff;
    white-space: nowrap;
    letter-spacing: 0.01em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    text-align: center;
    line-height: 1.1;
  }
}

/* Right City School Crest */
.city-crest {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 1.05cm;
  flex-shrink: 0;

  .city-crest-img {
    width: 27px;
    height: 28px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  .crest-since {
    font-size: 3.8pt;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;
    margin-top: 1.5px;
    letter-spacing: 0.02em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
    line-height: 1;
  }
}

/* =========================================
   CARD FRONT BODY & PHOTO
   ========================================= */
.card-body-front {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 12px;
  position: relative;
  z-index: 3;
  margin-top: -50px;
}

.photo-frame-wrapper {
  margin-bottom: 15px;
}

.photo-frame {
  width: 2.6cm;
  height: 2.6cm;
  border-radius: 18px;
  border: 2.5px solid $smart-red;
  background: #eef1f4;
  overflow: hidden;
  box-shadow: 0 0 0 3.5px #ffffff, 0 3px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .camera-icon {
      width: 28px;
      height: 28px;
      margin-bottom: 4px;
    }

    .photo-placeholder-text {
      font-size: 5.6pt;
      font-weight: 800;
      color: #7f8c8d;
      letter-spacing: 0.04em;
    }
  }
}

/* Student Fields */
.student-fields {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 7.5px;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.field-icon-box {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: $smart-red;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .field-icon {
    width: 13px;
    height: 13px;
  }
}

.field-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  .field-label {
    font-size: 5.8pt;
    font-weight: 800;
    color: #111111;
    letter-spacing: 0.03em;
    line-height: 1;
    margin-bottom: 1.5px;
  }

  .field-line {
    border-bottom: 1.2px solid $line-gray;
    min-height: 14px;
    display: flex;
    align-items: flex-end;
    padding-bottom: 1px;
  }

  .field-value {
    font-size: 7.6pt;
    font-weight: 700;
    color: #111111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.1;
  }

  &--triplet {
    flex-direction: row;
    align-items: stretch;
    gap: 4px;

    .triplet-col {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .triplet-divider {
      width: 1.2px;
      background: $line-gray;
      margin: 1px 2px 0;
    }
  }
}

/* =========================================
   BOTTOM WAVES & FOOTER (FRONT & BACK)
   ========================================= */
.bottom-waves-wrap {
  width: 100%;
  height: 18px;
  margin-top: auto;
  position: relative;
  z-index: 1;

  .bottom-waves-svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.card-footer-red {
  background: $smart-red;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  position: relative;
  z-index: 2;

  .footer-dash {
    height: 1.5px;
    background: #ffffff;
    width: 32px;
    opacity: 0.9;
  }

  .footer-title {
    font-size: 9.5pt;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.08em;
  }

  &--back {
    height: 34px;
    flex-direction: column;
    justify-content: center;
    gap: 1px;
    padding: 2px 0;

    .back-moto {
      font-size: 8.2pt;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: 0.06em;
      line-height: 1.15;
    }

    .back-project {
      font-size: 5.8pt;
      font-weight: 600;
      color: #ffffff;
      opacity: 0.95;
      letter-spacing: 0.02em;
      line-height: 1;
    }
  }
}

/* =========================================
   CARD BACK BODY
   ========================================= */
.card-body-back {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 12px;
  position: relative;
  z-index: 3;
}

/* Triple Stripes & Book Emblem */
.back-mid-stripes {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 35px;
  gap: 0;
}

.stripes-left,
.stripes-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.stripe-line {
  height: 1.6px;
  background: $smart-red;
  width: 100%;
}

.book-emblem-badge {
  padding: 0 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .book-circle-img {
    width: 48px;
    height: 48px;
    display: block;
    object-fit: contain;
  }
}

/* Back Return Info */
.back-return-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 8px;
  text-align: center;
}

.return-label {
  font-size: 7.2pt;
  font-weight: 800;
  color: #111111;
  margin-bottom: 6px;
}

.campus-main-entry {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 5px;

  .campus-main-pin {
    flex-shrink: 0;
    color: $smart-red;
    stroke-width: 2.5px;
  }

  .campus-main-title {
    font-size: 12pt;
    font-weight: 900;
    color: $smart-red;
    letter-spacing: -0.02em;
    line-height: 1;
    white-space: nowrap;
  }
}

.campus-address-entry {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
  padding: 0 6px;
  margin-bottom: 5px;

  .pin-icon-small {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 1px;

    .loc-pin-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .address-text {
    font-size: 6.4pt;
    font-weight: 700;
    color: #222222;
    line-height: 1.25;
    text-align: left;
  }
}

.back-divider-line {
  width: 90%;
  height: 1.2px;
  background: $line-gray;
  margin: 5px 0 7px;
}

.contact-entry {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding-left: 10px;
  margin-bottom: 4px;

  .contact-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    .mail-svg,
    .fb-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .contact-text {
    font-size: 6.8pt;
    font-weight: 700;
    color: #222222;
    letter-spacing: 0.01em;
  }
}

/* Loading, Error & Empty States */
.loading-wrapper,
.error-wrapper,
.no-data-state {
  background: white;
  border-radius: 20px;
  padding: 4rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

/* =========================================
   PRINT STYLES
   ========================================= */
@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  @page {
    size: A4 landscape;
    margin: 5mm 4mm 4mm 4mm;
  }

  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .smart-hassan-id-card-report {
    padding: 0 !important;
    background: white !important;
    min-height: 0 !important;
    height: auto !important;
  }

  .print-pages {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
  }

  .print-page {
    width: 100% !important;
    height: auto !important;
    padding-top: 2mm !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .print-page:not(:last-child) {
    page-break-after: always !important;
    break-after: page !important;
  }

  .print-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .print-page + .print-page {
    margin-top: 0 !important;
  }

  .cards-grid {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    align-content: start !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
    gap: 3mm 4mm !important;
  }

  .student-id-block {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    justify-content: center !important;
    width: auto !important;
    max-width: 100% !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    gap: 2mm !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .id-card {
    width: $card-w !important;
    height: $card-h !important;
    min-width: $card-w !important;
    min-height: $card-h !important;
    max-width: $card-w !important;
    max-height: $card-h !important;
    flex-shrink: 0 !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .card-header-red,
  .card-footer-red,
  .tss-badge,
  .city-crest,
  .field-icon-box,
  .photo-frame,
  .stripes-left .stripe-line,
  .stripes-right .stripe-line,
  .book-emblem-outer,
  .book-emblem-inner,
  .bottom-waves-wrap {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>

<style lang="scss">
@media print {
  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
}
</style>

